import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Loader2, Send, Check, Clock, X, Building2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  approved: { cls: "bg-[#5C8A72]/15 text-[#5C8A72]", icon: Check, label: "Approved" },
  pending: { cls: "bg-[#D98E73]/15 text-[#B5794E]", icon: Clock, label: "Pending review" },
  rejected: { cls: "bg-red-100 text-red-600", icon: X, label: "Not approved" },
};

export default function CenterMembershipPanel() {
  const qc = useQueryClient();
  const { data: centers = [], isLoading } = useQuery({
    queryKey: ["enh-centers"], queryFn: () => api.get("/enhancer/centers").then((r) => r.data),
  });
  const [applying, setApplying] = useState(null); // center being applied to (modal)
  const refresh = () => qc.invalidateQueries({ queryKey: ["enh-centers"] });

  const withdraw = async (c) => {
    if (!window.confirm(`Withdraw your application to ${c.name}?`)) return;
    try { await api.delete(`/enhancer/centers/${c.id}/apply`); toast.success("Application withdrawn"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="text-[#4A5D6B] text-sm">Loading centers…</div>;

  return (
    <div data-testid="enh-centers-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Work out of an Enhancing Your World center</h2>
      <p className="text-sm text-[#4A5D6B] mb-6">Apply to a center to schedule your sessions and retreats in its rooms. Once approved by the center's manager, you'll appear on its public page and can reserve its spaces.</p>
      {centers.length === 0 && <p className="text-[#4A5D6B] text-sm">No centers are open for applications yet.</p>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {centers.map((c) => {
          const st = STATUS[c.status];
          return (
            <div key={c.id} className="eyw-card overflow-hidden" data-testid={`enh-center-${c.id}`}>
              {c.image ? <img src={c.image} alt={c.name} className="h-32 w-full object-cover" /> : <div className="h-32 bg-[#144F75]/10 grid place-items-center"><Building2 size={28} className="text-[#144F75]/40" /></div>}
              <div className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.kind === "flagship" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{c.kind}</span>
                  {st && <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${st.cls}`}><st.icon size={11} /> {st.label}</span>}
                </div>
                <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-1.5"><MapPin size={15} className="text-[#D98E73]" /> {c.name}</h3>
                <p className="text-xs text-[#4A5D6B] mt-0.5">{[c.city, c.state].filter(Boolean).join(", ")}</p>
                <p className="text-xs text-[#8FA89B] mt-2">Split: {c.enhancer_pct}% you / {c.ecosystem_pct}% ecosystem</p>
                <div className="mt-4">
                  {(c.status === "none" || c.status === "rejected") && (
                    <button onClick={() => setApplying(c)} data-testid={`enh-apply-${c.id}`} className="eyw-btn !py-2 text-sm w-full"><Send size={14} /> {c.status === "rejected" ? "Re-apply" : "Apply to work here"}</button>
                  )}
                  {c.status === "pending" && (
                    <button onClick={() => withdraw(c)} data-testid={`enh-withdraw-${c.id}`} className="eyw-btn-ghost !py-2 text-sm w-full">Withdraw application</button>
                  )}
                  {c.status === "approved" && (
                    <p className="text-xs text-center text-[#5C8A72]">Book time here from "Center calendars".</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {applying && <ApplyModal center={applying} onClose={() => setApplying(null)} onDone={refresh} />}
    </div>
  );
}

function ApplyModal({ center, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/enhancer/centers/${center.id}/apply`, { message }); toast.success(`Application sent to ${center.name}`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="enh-apply-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1">Apply to {center.name}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Tell the center manager a bit about the work you'd offer here (optional).</p>
        <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="I offer breathwork and sound healing…" data-testid="enh-apply-message" className="eyw-input w-full resize-none" />
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="enh-apply-submit" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Send application</button>
        </div>
      </div>
    </div>
  );
}
